import { useSessionStore } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  PanelError,
  PaperPanel,
  Skeleton,
  Swipe,
} from "@scribenote/brian-design-system";
import { useNavigate } from "@tanstack/react-router";
import {
  KNOWN_TASK_KINDS,
  type TaskCounts,
  taskKindLabel,
} from "@tasks/tasksClient";
import { useTaskCounts } from "@tasks/tasksQueries";
import { useMemo } from "react";

/** Known kinds in display order; an unlisted kind sorts last rather than being
 * dropped, so the rows still add up to the total. */
const kindRank = (kind: string) => {
  const index = KNOWN_TASK_KINDS.indexOf(kind);
  return index === -1 ? KNOWN_TASK_KINDS.length : index;
};

/** The endpoint's kind keys as rows, in display order. No folding: since BE-2381
 * every key it sends is a row a person can open. */
const kindCountRows = (counts: TaskCounts) =>
  Object.entries(counts)
    .filter(([, c]) => c.open > 0)
    .sort(([a], [b]) => kindRank(a) - kindRank(b));

/**
 * A per-user at-a-glance summary above the room grid (WEB-3222): the caller's own open
 * tasks by type, each row showing how many are open and, in red, how many of those the
 * agent has flagged urgent (BE-2133; a severity signal now, not the due-date count it
 * replaced). Done and cancelled are history and don't appear. Every count links to the
 * matching filtered list, so the pile can be actioned rather than just seen.
 *
 * Counts come from `GET /tasks/counts` (BE-1989), scoped to the caller's own user/role
 * set (BE-2089) and exact rather than derived from a paginated list, kept fresh by
 * websocket invalidation of the `["tasks"]` prefix.
 */
export const ClinicSummary = () => (
  <AsyncBoundary
    skeleton={<SummarySkeleton />}
    fallback={(retry) => <PanelError label="summary" onRetry={retry} />}
  >
    <ClinicSummaryInner />
  </AsyncBoundary>
);

const ClinicSummaryInner = () => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  // Scoped to the caller's own tasks (the hook default), passed explicitly so
  // this summary's scope is a deliberate choice rather than an inherited
  // default that could silently change underneath it.
  const { data: counts } = useTaskCounts(sessionKey, "me");
  const navigate = useNavigate();

  const { openCount, urgentCount, kindCounts } = useMemo(() => {
    const kindCounts = kindCountRows(counts);
    let openCount = 0;
    let urgentCount = 0;
    for (const [, c] of kindCounts) {
      openCount += c.open;
      urgentCount += c.urgent;
    }
    return { openCount, urgentCount, kindCounts };
  }, [counts]);

  return (
    <PaperPanel
      variant="card"
      className="p-2"
      data-testid="clinic-summary"
      data-intercom-target="clinic-summary"
    >
      <div className="flex flex-col divide-y divide-rule/50">
        <CountRow
          emphasis
          label="Open tasks"
          count={openCount}
          urgent={urgentCount}
          dataTestId="clinic-summary-open"
          onOpen={() => navigate({ to: "/tasks" })}
          onUrgent={() => navigate({ to: "/tasks", search: { urgent: true } })}
        />
        {kindCounts.map(([kind, counts]) => (
          <CountRow
            key={kind}
            label={taskKindLabel(kind)}
            count={counts.open}
            urgent={counts.urgent}
            dataTestId={`clinic-summary-kind-${kind}`}
            onOpen={() => navigate({ to: "/tasks", search: { kind } })}
            onUrgent={() =>
              navigate({ to: "/tasks", search: { kind, urgent: true } })
            }
          />
        ))}
      </div>
    </PaperPanel>
  );
};

/**
 * A count row: an open count (tappable to that list) and, when any are urgent,
 * a stopped-marker count beside it (tappable to that urgent subset) - two
 * separate targets so the urgent signal stays actionable on its own. The
 * emphasised variant is the "Open tasks" parent (the user's own totals); the
 * plain variant is an indented per-type breakdown row.
 *
 * The open count is plain tabular data, not a status, so it stays unbadged. The
 * urgent count carries its word under the highlighter: a wash never appears
 * without saying what it means.
 */
const CountRow = ({
  label,
  count,
  urgent,
  dataTestId,
  onOpen,
  onUrgent,
  emphasis = false,
}: {
  label: string;
  count: number;
  urgent: number;
  dataTestId: string;
  onOpen: () => void;
  onUrgent: () => void;
  emphasis?: boolean;
}) => (
  // A fixed urgent column keeps the open-count edge from moving between rows;
  // both number spans right-align tabular figures inside an equal-width field.
  <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] items-center gap-2">
    <Button
      variant="ghost"
      size="sm"
      onClick={onOpen}
      data-testid={dataTestId}
      className={`min-w-0 flex-1 justify-start font-normal ${emphasis ? "" : "pl-8"}`}
    >
      <span
        className={
          emphasis
            ? "min-w-0 flex-1 truncate text-left text-annotation-caps text-ink"
            : "min-w-0 flex-1 truncate text-left text-sm text-ink-body"
        }
      >
        {label}
      </span>
      <span
        className="w-[3ch] shrink-0 text-right font-mono text-[0.8125rem] font-medium tabular-nums text-ink"
        data-testid={`${dataTestId}-count`}
      >
        {count}
      </span>
    </Button>
    <div className="flex min-w-0 justify-start">
      {urgent > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onUrgent}
          data-testid={`${dataTestId}-urgent`}
          aria-label={`${urgent} urgent`}
          className="w-full justify-start px-1.5"
        >
          <Swipe
            tone="stopped"
            className="inline-flex items-center gap-1 whitespace-nowrap text-annotation-caps"
            data-testid={`${dataTestId}-urgent-count`}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
            <span className="w-[3ch] text-right tabular-nums">{urgent}</span>
            urgent
          </Swipe>
        </Button>
      )}
    </div>
  </div>
);

const SummarySkeleton = () => <Skeleton className="h-56 border border-rule" />;
